import { useChapter } from '@/context/ChapterContext'
import React, { useEffect, useRef, useState } from 'react'
import './ChapterScroll.css'

interface ChapterScrollProps {
  children: React.ReactNode[]
  selectedChapter?: number // Allow overriding the chapter to scroll to
}

const ChapterScroll: React.FC<ChapterScrollProps> = ({ children, selectedChapter }) => {
  const { currentChapterNumber, setCurrentChapter } = useChapter()
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const [containerWidth, setContainerWidth] = useState(0)
  // Use selectedChapter prop if provided, otherwise fall back to currentChapterNumber
  const chapterToShow = selectedChapter ?? currentChapterNumber
  // Derive active index from context (1-based to 0-based)
  const activeIndex = Math.max(0, (chapterToShow || 1) - 1)

  // Track container width for dynamic padding
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const moveIt = React.useCallback(() => {
    const container = containerRef.current
    const activeItem = itemRefs.current[activeIndex]
    if (!container || !activeItem) return

    const scrollLeft = activeItem.offsetLeft - container.offsetWidth / 2 + activeItem.offsetWidth / 2
    container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
  }, [activeIndex])

  // Center the active item immediately on index change
  useEffect(() => {
    moveIt()
  }, [moveIt])

  // Observe active item size changes and update scroll position
  useEffect(() => {
    const container = containerRef.current
    const activeItem = itemRefs.current[activeIndex]
    if (!container || !activeItem) return

    moveIt()
    let resizeTimeout: ReturnType<typeof setTimeout>
    const resizeObserver = new window.ResizeObserver(() => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        moveIt()
      }, 40)
    })
    resizeObserver.observe(activeItem)

    return () => {
      resizeObserver.disconnect()
      clearTimeout(resizeTimeout)
    }
  }, [moveIt, activeIndex])

  // Selection is controlled via ChapterContext by consumers (e.g., child buttons)

  return (
    <div className="chapter-scroll-container" ref={containerRef}>
      <div
        className="chapter-scroll-list"
        style={{
          paddingLeft: containerWidth ? `${containerWidth / 2}px` : undefined,
          paddingRight: containerWidth ? `${containerWidth / 2}px` : undefined,
        }}
      >
        {React.Children.map(children, (child, idx) => {
          const childKey = React.isValidElement(child) && child.key != null ? String(child.key) : undefined
          return (
            <div
              className={`chapter-scroll-item${idx === activeIndex ? ' active' : ''}`}
              ref={(el) => {
                itemRefs.current[idx] = el
              }}
              aria-selected={idx === activeIndex}
              key={childKey}
            >
              {child}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ChapterScroll
